import { css, html, LitElement, PropertyValues, TemplateResult } from "lit";
import { customElement } from "lit/decorators/custom-element.js";
import { sharedStyles } from "../../styles/shared";
import "../../atoms/form-item";
import "../../atoms/text";
import "../../atoms/seo/seo-link";
import "../../atoms/confetti-wall";
import "../../atoms/card";
import "../../atoms/section";
import "../../atoms/icon";
import "../../atoms/list-item";
import "../../atoms/title";
import "../../atoms/quote-card";
import "../../atoms/card-pagination";
import "../../molecules/landing/landing-title-header";
import "../../atoms/section-split";
import "./landing-get-started-page";
import { ICON_CHEVRON_RIGHT } from "../../../icons";
import { repeat } from "lit/directives/repeat.js";
import { addRouteChangedListener, pushState, removeRouteChangedListener } from "../../util/route-util";
import { ROUTES } from "../../config";
import { pickRandomItems } from "shared";
import { getStarted } from "../../util/landing-util";
import { ifDefined } from "lit/directives/if-defined.js";
import { ALL_FEATURES, FeatureCardItem, FeatureCollection } from "../../data/all-features";
import { get, translate } from "lit-translate";
import { state } from "lit/decorators/state.js";

@customElement("bs-landing-features-page")
class BsLandingFeaturesPage extends LitElement {
	static get styles() {
		return [
			sharedStyles,
			css`
				:host {
				}

				.feature-group {
				}

				#feature-groups {
					display: flex;
					flex-direction: column;
					gap: 100px;
					padding: 0 var(--space-l) var(--space-xxl);
				}

				#other-features-section,
				#feature-groups-section {
					--section-space-top: 0;
				}

				#other-features {
					display: grid;
					grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
					gap: var(--space-l);
				}

				.other-feature {
					display: flex;
					align-items: center;
					justify-content: space-between;
				}

				.other-feature > * {
					display: flex;
					align-items: center;
					gap: var(--space-m);
				}

				#feature-section {
					--section-split-illustration-box-shadow: var(--shadow) 0px 7px 10px -3px, var(--shadow) 0px -2px 6px -2px;
				}
			`
		];
	}

	private boundRouteChanged = this.routeChanged.bind(this);
	@state() featureCollections: FeatureCollection[] = ALL_FEATURES();

	protected firstUpdated(props: PropertyValues) {
		super.firstUpdated(props);
		addRouteChangedListener(this.boundRouteChanged);
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		removeRouteChangedListener(this.boundRouteChanged);
	}

	routeChanged() {
		this.requestUpdate();
		this.updateDocumentTitle();
	}

	updateDocumentTitle() {
		const feature = this.getSelectedFeature();
		if (feature != null) {
			document.title = `Ideamap | Features | ${feature.name}`;
		}
	}

	getStarted(ref?: string, feature?: string) {
		getStarted(ref, feature);
	}

	getSelectedFeature(): FeatureCardItem | undefined {
		const selectedId = window.location.pathname.split("/")?.[2];
		return this.featureCollections.flatMap((feature) => feature.items).find((feature) => feature.id === selectedId);
	}

	renderCard(item: FeatureCardItem) {
		const { name, id } = item;
		const { text, illustration, soon } = item.data;
		return html`
			<bs-seo-link href="${ROUTES.getStarted}" no-routing>
				<bs-quote-card
					id="quote-card"
					headline="${name}"
					text="${text}"
					flip
					illustration="/assets/landing/features/${illustration}"
					hoverable
					chip="${ifDefined(soon ? get("Coming soon") : undefined)}"
					size="large"
					cta="${translate("Get started with Ideamap")}"
					@click="${() => this.getStarted(`feature_${id}`, id)}"
				></bs-quote-card>
			</bs-seo-link>
		`;
	}

	renderFeature(feature: FeatureCardItem): TemplateResult {
		const { name, icon } = feature;
		const { text, illustration, soon } = feature.data;
		const otherFeatures: FeatureCardItem[] = pickRandomItems(
			this.featureCollections.flatMap((collection) => collection.items).filter(({ id }) => id !== feature.id),
			3
		);

		return html`
			<bs-section-split
				id="feature-section"
				.icon="${icon}"
				headline="${name}"
				text="${text}"
				headline-level="1"
				illustration="/assets/landing/features/${illustration}"
			></bs-section-split>
			<bs-section id="other-features-section">
				<bs-landing-title-header label="Other useful features" margin></bs-landing-title-header>
				<div id="other-features">
					${repeat(
						otherFeatures,
						({ id, name, icon }) => html`
							<bs-seo-link href="${ROUTES.features(id)}" no-routing>
								<bs-card size="large" hoverable @click="${() => this.selectFeature(id)}">
									<article class="other-feature">
										<div>
											<bs-icon .icon="${icon}"></bs-icon>
											<bs-text>${name}</bs-text>
										</div>
										<bs-icon .icon="${ICON_CHEVRON_RIGHT}"></bs-icon>
									</article>
								</bs-card>
							</bs-seo-link>
						`
					)}
				</div>
			</bs-section>
		`;
	}

	selectFeature(id: string) {
		pushState(ROUTES.features(id));
	}

	render() {
		const selectedFeature = this.getSelectedFeature();
		if (selectedFeature != null) {
			return this.renderFeature(selectedFeature);
		}

		console.log(this.featureCollections);

		return html`
			<bs-section-split
				headline="${translate("One platform for all your brainstorming")}"
				text="${translate("Ideamap offers a comprehensive suite of features for effective brainstorming")}"
				illustration="/assets/landing/features/features.webp"
				headline-level="1"
			></bs-section-split>
			<bs-section id="feature-groups-section">
				<div id="feature-groups">
					${repeat(
						this.featureCollections,
						({ name, items }) => html`
							<div class="feature-group">
								<bs-landing-title-header label="${name}" margin></bs-landing-title-header>
								<bs-card-pagination
									.items="${items.filter((item) => !item.hidden)}"
									.renderCard="${this.renderCard.bind(this)}"
								></bs-card-pagination>
							</div>
						`
					)}
				</div>
			</bs-section>
		`;
	}
}
